/**
 * REWARDED AD SERVICE - Tanzeen+
 * 
 * Google AdMob Rewarded Video Ads Integration
 * "Watch Ad to Unlock" feature implementation
 * 
 * App ID: ca-app-pub-2976157850474962~5544781422
 * Rewarded Ad Unit ID: ca-app-pub-2976157850474962/3456945186
 * 
 * ============================================
 * MONETIZATION MODEL (Revenue Optimized)
 * ============================================
 * 
 * FREE USERS:
 * - Watch ads to unlock features temporarily
 * - Daily ad limit: 5 ads/day (then must upgrade)
 * - Unlock types:
 *   • SCAN features: 1 ad = 1 scan
 *   • STANDARD features: 1 ad = 3 uses
 *   • TIME-BASED features: 1 ad = 5 minutes
 * 
 * PRO USERS (RevenueCat):
 * - No ads, unlimited access
 * - All features unlocked
 */

import { Capacitor } from '@capacitor/core';

// ============================================
// AD CONFIGURATION
// ============================================
export const REWARDED_AD_CONFIG = {
  // Production App ID - SET VIA ENVIRONMENT VARIABLE
  APP_ID: process.env.REACT_APP_ADMOB_APP_ID || 'ca-app-pub-XXXXX~YYYYY',
  
  // Production Rewarded Ad Unit ID - SET VIA ENVIRONMENT VARIABLE
  REWARDED_AD_UNIT: process.env.REACT_APP_ADMOB_REWARDED_UNIT || 'ca-app-pub-XXXXX/YYYYY',
  
  // Google Demo/Test Ad Unit IDs (for development)
  TEST_REWARDED_AD_UNIT: 'ca-app-pub-3940256099942544/5224354917',
  
  // Enable test mode in development
  TEST_MODE: process.env.NODE_ENV === 'development',
  
  // Minimum time between ad loads (prevent spam)
  MIN_LOAD_INTERVAL_MS: 10000, // 10 seconds
  
  // DAILY AD LIMIT - Free users can watch max 5 ads per day
  MAX_ADS_PER_DAY: 5,
};

// ============================================
// UNLOCK TYPES - Configure per feature
// ============================================
export const UNLOCK_TYPES = {
  // 1 ad = 1 use (for high-value features like scans)
  SINGLE_USE: 'single_use',
  
  // 1 ad = X uses (for standard features)
  MULTI_USE: 'multi_use',
  
  // 1 ad = X minutes/hours access (for browsing features)
  TIME_BASED: 'time_based',
  
  // 1 ad = until app closes (legacy, not recommended)
  SESSION: 'session',
  
  // PRO ONLY - No ad unlock available (must subscribe)
  PRO_ONLY: 'pro_only',
};

// ============================================
// FEATURE UNLOCK CONFIGURATION
// ============================================
// STRICT ADMOB GATING RULES (Dec 2025):
// - 1 Ad = Precise Reward (no leaks)
// - Counters do NOT reset daily - only after watching another ad
// - Time-based features show visible countdown
// - Hard timeouts prevent infinite loading
// ============================================
export const FEATURE_UNLOCK_CONFIG = {
  // ========================================
  // PRO-ONLY FEATURES (No Ad Unlock Available)
  // ========================================
  
  // Halal Scanner - PRO ONLY (per user request Dec 2025)
  halal_scanner: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Mosque Finder/Search - PRO ONLY (per user request Dec 2025)
  mosque_finder: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  mosque_search: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Smart Mosque Connect / Prayer Extraction - PRO ONLY (per user request Dec 2025)
  smart_mosque_connect: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  prayer_extraction: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Masjid Connect - PRO ONLY (per user request Dec 2025)
  masjid_connect: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // ========================================
  // ADMOB REWARDED FEATURES (Keep Ad Unlock)
  // ========================================
  
  // Dua Generator - 1 ad = 3 generations (AI feature - keep ad unlock)
  dua_generator: {
    type: UNLOCK_TYPES.MULTI_USE,
    uses: 3,
    label: '3 generations',
    hardTimeoutSeconds: 30,
  },
  custom_dua: {
    type: UNLOCK_TYPES.MULTI_USE,
    uses: 3,
    label: '3 generations',
    hardTimeoutSeconds: 30,
  },
  
  // Tajweed Coach - 1 ad = 100 tokens (AI feature - keep ad unlock)
  tajweed_coach: {
    type: UNLOCK_TYPES.MULTI_USE,
    uses: 100,
    label: '100 Tokens',
    hardTimeoutSeconds: 10,
  },
  
  // Zoom Mode - 1 ad = 30 minutes (with visible countdown)
  zoom_mode: {
    type: UNLOCK_TYPES.TIME_BASED,
    minutes: 30,
    label: '30 minutes',
    showCountdown: true,
  },
  
  // Tafsir - 1 ad = 3 uses (keep ad unlock)
  tafsir: {
    type: UNLOCK_TYPES.MULTI_USE,
    uses: 3,
    label: '3 views',
  },
  
  // Qibla Styles - PRO ONLY (per user request Dec 2025)
  all_qibla_styles: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Hadith Books - PRO ONLY (per user request Dec 2025)
  all_hadith_books: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Offline Download - PRO ONLY (per user request Dec 2025)
  offline: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro subscription required',
  },
  
  // Khatm Calendar - 1 ad = 5 minutes
  khatm_calendar: {
    type: UNLOCK_TYPES.TIME_BASED,
    minutes: 5,
    label: '5 minutes',
  },
  
  // Reading Stats - 1 ad = 5 minutes
  reading_stats: {
    type: UNLOCK_TYPES.TIME_BASED,
    minutes: 5,
    label: '5 minutes',
  },
  
  // ========================================
  // REVENUECAT PRO ONLY (NO AD UNLOCK!)
  // ========================================
  
  // Night Vision - STRICT PRO ONLY (NO AD UNLOCK)
  night_vision: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Theme Selection - STRICT PRO ONLY (NO AD UNLOCK)
  premium_themes: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  all_themes: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Automatic Midnight Refresh - STRICT PRO ONLY (NO AD UNLOCK)
  midnight_refresh: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  auto_refresh: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Widget Menu - PRO ONLY (no ad unlock)
  widget_menu: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Widget Mode - PRO ONLY (no ad unlock)
  widget_mode: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Bottom Nav - PRO ONLY (no ad unlock)
  bottom_nav: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // Widget Styles & Dynamic Icons - PRO ONLY (no ad unlock)
  widget_styles: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
  
  // All Reciters - PRO ONLY
  all_reciters: {
    type: UNLOCK_TYPES.PRO_ONLY,
    label: 'Pro only',
    noAdUnlock: true,
  },
};

// Default config for unknown features
const DEFAULT_UNLOCK_CONFIG = {
  type: UNLOCK_TYPES.MULTI_USE,
  uses: 3,
  label: '3 uses',
};

// ============================================
// STORAGE KEYS
// ============================================
const STORAGE_KEYS = {
  // Daily ad count (localStorage - persists across sessions)
  DAILY_AD_COUNT: 'tanzeel_daily_ad_count',
  DAILY_AD_DATE: 'tanzeel_daily_ad_date',
  
  // Feature unlock data (localStorage for time-based, sessionStorage for session-based)
  UNLOCK_PREFIX: 'tanzeel_unlock_',
};

// Get today's date key
const getTodayKey = () => new Date().toISOString().split('T')[0];

// Check if running in native Capacitor environment
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// ============================================
// DAILY AD LIMIT TRACKING
// ============================================

/**
 * Get today's ad count
 */
export const getDailyAdCount = () => {
  try {
    const today = getTodayKey();
    const savedDate = localStorage.getItem(STORAGE_KEYS.DAILY_AD_DATE);
    
    // Reset if new day
    if (savedDate !== today) {
      localStorage.setItem(STORAGE_KEYS.DAILY_AD_DATE, today);
      localStorage.setItem(STORAGE_KEYS.DAILY_AD_COUNT, '0');
      return 0;
    }
    
    return parseInt(localStorage.getItem(STORAGE_KEYS.DAILY_AD_COUNT) || '0', 10);
  } catch {
    return 0;
  }
};

/**
 * Increment daily ad count
 */
const incrementDailyAdCount = () => {
  try {
    const today = getTodayKey();
    localStorage.setItem(STORAGE_KEYS.DAILY_AD_DATE, today);
    const currentCount = getDailyAdCount();
    localStorage.setItem(STORAGE_KEYS.DAILY_AD_COUNT, (currentCount + 1).toString());
    return currentCount + 1;
  } catch {
    return 0;
  }
};

/**
 * Check if user can watch more ads today
 */
export const canWatchMoreAds = () => {
  const count = getDailyAdCount();
  return count < REWARDED_AD_CONFIG.MAX_ADS_PER_DAY;
};

/**
 * Get remaining ads for today
 */
export const getRemainingAdsToday = () => {
  const count = getDailyAdCount();
  return Math.max(0, REWARDED_AD_CONFIG.MAX_ADS_PER_DAY - count);
};

// ============================================
// FEATURE UNLOCK TRACKING
// ============================================

/**
 * Get unlock config for a feature
 */
export const getFeatureUnlockConfig = (featureId) => {
  return FEATURE_UNLOCK_CONFIG[featureId] || DEFAULT_UNLOCK_CONFIG;
};

/**
 * Check if a feature is PRO_ONLY (no ad unlock available)
 */
export const isFeatureProOnly = (featureId) => {
  const config = getFeatureUnlockConfig(featureId);
  return config.type === UNLOCK_TYPES.PRO_ONLY || config.noAdUnlock === true;
};

/**
 * Get hard timeout for a feature process (in seconds)
 * Used to prevent infinite loading loops
 */
export const getFeatureHardTimeout = (featureId) => {
  const config = getFeatureUnlockConfig(featureId);
  return config.hardTimeoutSeconds || 30; // Default 30 seconds
};

/**
 * Check if feature should show countdown timer
 */
export const shouldShowCountdown = (featureId) => {
  const config = getFeatureUnlockConfig(featureId);
  return config.type === UNLOCK_TYPES.TIME_BASED && config.showCountdown === true;
};

/**
 * Get unlock storage key for a feature
 */
const getUnlockKey = (featureId) => `${STORAGE_KEYS.UNLOCK_PREFIX}${featureId}`;

/**
 * Save feature unlock after watching ad
 */
export const saveFeatureUnlock = (featureId) => {
  try {
    const config = getFeatureUnlockConfig(featureId);
    const key = getUnlockKey(featureId);
    
    const unlockData = {
      featureId,
      type: config.type,
      timestamp: Date.now(),
    };
    
    switch (config.type) {
      case UNLOCK_TYPES.SINGLE_USE:
        unlockData.usesRemaining = config.uses || 1;
        localStorage.setItem(key, JSON.stringify(unlockData));
        break;
        
      case UNLOCK_TYPES.MULTI_USE:
        unlockData.usesRemaining = config.uses || 3;
        localStorage.setItem(key, JSON.stringify(unlockData));
        break;
        
      case UNLOCK_TYPES.TIME_BASED:
        const minutes = config.minutes || 5;
        unlockData.expiresAt = Date.now() + (minutes * 60 * 1000);
        localStorage.setItem(key, JSON.stringify(unlockData));
        break;
        
      case UNLOCK_TYPES.SESSION:
      default:
        // Session-based - use sessionStorage
        sessionStorage.setItem(key, JSON.stringify(unlockData));
        break;
    }
    
    // Increment daily ad count
    incrementDailyAdCount();
    
    console.log(`[RewardedAd] Feature ${featureId} unlocked:`, config.label);
    return true;
  } catch (e) {
    console.error('[RewardedAd] Failed to save unlock:', e);
    return false;
  }
};

/**
 * Check if feature is unlocked (and has uses/time remaining)
 */
export const isFeatureUnlocked = (featureId) => {
  try {
    const config = getFeatureUnlockConfig(featureId);
    const key = getUnlockKey(featureId);
    
    let data;
    if (config.type === UNLOCK_TYPES.SESSION) {
      const stored = sessionStorage.getItem(key);
      if (!stored) return false;
      data = JSON.parse(stored);
    } else {
      const stored = localStorage.getItem(key);
      if (!stored) return false;
      data = JSON.parse(stored);
    }
    
    switch (config.type) {
      case UNLOCK_TYPES.SINGLE_USE:
      case UNLOCK_TYPES.MULTI_USE:
        return (data.usesRemaining || 0) > 0;
        
      case UNLOCK_TYPES.TIME_BASED:
        return Date.now() < (data.expiresAt || 0);
        
      case UNLOCK_TYPES.SESSION:
      default:
        return true; // Session storage exists = unlocked
    }
  } catch {
    return false;
  }
};

/**
 * Consume one use of a feature (call when feature is used)
 */
export const consumeFeatureUse = (featureId) => {
  try {
    const config = getFeatureUnlockConfig(featureId);
    const key = getUnlockKey(featureId);
    
    // Only consume uses for use-based unlock types
    if (config.type !== UNLOCK_TYPES.SINGLE_USE && config.type !== UNLOCK_TYPES.MULTI_USE) {
      return true;
    }
    
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    
    const data = JSON.parse(stored);
    if ((data.usesRemaining || 0) <= 0) return false;
    
    data.usesRemaining -= 1;
    
    if (data.usesRemaining <= 0) {
      // No more uses - remove unlock
      localStorage.removeItem(key);
      console.log(`[RewardedAd] Feature ${featureId} uses exhausted`);
    } else {
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`[RewardedAd] Feature ${featureId} uses remaining: ${data.usesRemaining}`);
    }
    
    return true;
  } catch {
    return false;
  }
};

/**
 * Get remaining uses/time for a feature
 */
export const getFeatureUnlockStatus = (featureId) => {
  try {
    const config = getFeatureUnlockConfig(featureId);
    const key = getUnlockKey(featureId);
    
    let data;
    if (config.type === UNLOCK_TYPES.SESSION) {
      const stored = sessionStorage.getItem(key);
      if (!stored) return { unlocked: false };
      data = JSON.parse(stored);
    } else {
      const stored = localStorage.getItem(key);
      if (!stored) return { unlocked: false };
      data = JSON.parse(stored);
    }
    
    switch (config.type) {
      case UNLOCK_TYPES.SINGLE_USE:
      case UNLOCK_TYPES.MULTI_USE:
        return {
          unlocked: (data.usesRemaining || 0) > 0,
          usesRemaining: data.usesRemaining || 0,
          type: config.type,
        };
        
      case UNLOCK_TYPES.TIME_BASED:
        const timeRemaining = Math.max(0, (data.expiresAt || 0) - Date.now());
        return {
          unlocked: timeRemaining > 0,
          timeRemaining,
          minutesRemaining: Math.ceil(timeRemaining / 60000),
          type: config.type,
        };
        
      case UNLOCK_TYPES.SESSION:
      default:
        return { unlocked: true, type: config.type };
    }
  } catch {
    return { unlocked: false };
  }
};

/**
 * Clear all ad-based unlocks
 */
export const clearAllUnlocks = () => {
  try {
    // Clear from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEYS.UNLOCK_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear from sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(STORAGE_KEYS.UNLOCK_PREFIX)) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Also clear legacy keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('pro_ad_unlock_')) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    console.log('[RewardedAd] All unlocks cleared');
  } catch (e) {
    console.error('[RewardedAd] Failed to clear unlocks:', e);
  }
};

// ============================================
// REWARDED AD SERVICE CLASS
// ============================================

/**
 * RewardedAdService - Manages Google AdMob Rewarded Video Ads
 */
class RewardedAdService {
  constructor() {
    this.initialized = false;
    this.admob = null;
    this.isAdLoaded = false;
    this.isAdLoading = false;
    this.isAdShowing = false;
    this.lastLoadTime = 0;
    this.listeners = [];
    this.pendingRewardCallback = null;
    this.eventListenerHandles = [];
  }

  /**
   * Initialize the AdMob service
   */
  async initialize() {
    if (this.initialized) return true;

    try {
      if (isNativeApp()) {
        try {
          // Dynamic import of AdMob plugin (only available in native builds)
          const { AdMob, RewardAdPluginEvents } = await import('@capacitor-community/admob');
          this.admob = AdMob;
          this.RewardAdPluginEvents = RewardAdPluginEvents;
          
          // Initialize AdMob
          await this.admob.initialize({
            requestTrackingAuthorization: true,
            testingDevices: REWARDED_AD_CONFIG.TEST_MODE ? ['TEST_DEVICE_ID'] : [],
            initializeForTesting: REWARDED_AD_CONFIG.TEST_MODE,
          });
          
          // Set up event listeners
          await this.setupEventListeners();
          
          console.log('[RewardedAd] AdMob initialized successfully');
          this.initialized = true;
          return true;
        } catch (e) {
          console.log('[RewardedAd] AdMob not available (running in web?):', e.message);
        }
      }
      
      // Web mode - use mock implementation
      console.log('[RewardedAd] Running in web mode - using mock ads');
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('[RewardedAd] Initialization error:', error);
      this.initialized = true;
      return false;
    }
  }

  /**
   * Set up AdMob event listeners
   */
  async setupEventListeners() {
    if (!this.admob || !this.RewardAdPluginEvents) return;

    try {
      // Ad Loaded
      const loadedHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.Loaded,
        () => {
          console.log('[RewardedAd] Ad loaded successfully');
          this.isAdLoaded = true;
          this.isAdLoading = false;
          this.notifyListeners('loaded');
        }
      );
      this.eventListenerHandles.push(loadedHandle);

      // Failed to Load
      const failedLoadHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.FailedToLoad,
        (error) => {
          console.error('[RewardedAd] Failed to load:', error);
          this.isAdLoaded = false;
          this.isAdLoading = false;
          this.notifyListeners('failedToLoad', error);
        }
      );
      this.eventListenerHandles.push(failedLoadHandle);

      // Ad Showed
      const showedHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.Showed,
        () => {
          console.log('[RewardedAd] Ad showed');
          this.isAdShowing = true;
          this.notifyListeners('showed');
        }
      );
      this.eventListenerHandles.push(showedHandle);

      // Failed to Show
      const failedShowHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.FailedToShow,
        (error) => {
          console.error('[RewardedAd] Failed to show:', error);
          this.isAdShowing = false;
          this.notifyListeners('failedToShow', error);
        }
      );
      this.eventListenerHandles.push(failedShowHandle);

      // Ad Dismissed
      const dismissedHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.Dismissed,
        () => {
          console.log('[RewardedAd] Ad dismissed');
          this.isAdLoaded = false;
          this.isAdShowing = false;
          this.notifyListeners('dismissed');
          // Preload next ad
          this.preloadAd();
        }
      );
      this.eventListenerHandles.push(dismissedHandle);

      // Reward Earned - THE KEY EVENT!
      const rewardHandle = await this.admob.addListener(
        this.RewardAdPluginEvents.Rewarded,
        (reward) => {
          console.log('[RewardedAd] Reward earned:', reward);
          this.notifyListeners('rewarded', reward);
          // Call pending callback if any
          if (this.pendingRewardCallback) {
            this.pendingRewardCallback(reward);
            this.pendingRewardCallback = null;
          }
        }
      );
      this.eventListenerHandles.push(rewardHandle);

      console.log('[RewardedAd] Event listeners set up');
    } catch (error) {
      console.error('[RewardedAd] Error setting up listeners:', error);
    }
  }

  /**
   * Preload a rewarded ad
   */
  async preloadAd() {
    if (!this.initialized) {
      await this.initialize();
    }

    // Prevent spam loading
    const now = Date.now();
    if (now - this.lastLoadTime < REWARDED_AD_CONFIG.MIN_LOAD_INTERVAL_MS) {
      console.log('[RewardedAd] Too soon to load another ad');
      return false;
    }

    if (this.isAdLoading || this.isAdLoaded) {
      console.log('[RewardedAd] Ad already loading or loaded');
      return this.isAdLoaded;
    }

    try {
      this.isAdLoading = true;
      this.lastLoadTime = now;

      if (this.admob) {
        // Native: Load real ad
        const adUnitId = REWARDED_AD_CONFIG.TEST_MODE
          ? REWARDED_AD_CONFIG.TEST_REWARDED_AD_UNIT
          : REWARDED_AD_CONFIG.REWARDED_AD_UNIT;

        await this.admob.prepareRewardVideoAd({
          adId: adUnitId,
          isTesting: REWARDED_AD_CONFIG.TEST_MODE,
          npa: false,
        });

        console.log('[RewardedAd] Ad preload started');
        return true;
      } else {
        // Web: Mock ad is always "loaded"
        this.isAdLoaded = true;
        this.isAdLoading = false;
        console.log('[RewardedAd] Mock ad ready (web mode)');
        return true;
      }
    } catch (error) {
      console.error('[RewardedAd] Preload error:', error);
      this.isAdLoading = false;
      return false;
    }
  }

  /**
   * Show the rewarded ad
   * @param {Function} onRewardEarned - Callback when user earns reward
   * @returns {Promise<boolean>} - True if ad was shown
   */
  async showAd(onRewardEarned) {
    if (!this.initialized) {
      await this.initialize();
    }

    // Check daily limit
    if (!canWatchMoreAds()) {
      console.log('[RewardedAd] Daily ad limit reached');
      this.notifyListeners('dailyLimitReached');
      return false;
    }

    try {
      this.pendingRewardCallback = onRewardEarned;

      if (this.admob) {
        // Native: Show real ad.
        // If an ad isn't preloaded yet, kick off a preload and poll for up to
        // 8s in 250ms slices - waiting a flat 2s was too short for a real AdMob
        // fetch on cellular, which produced the "Unable to load ad" loop for
        // features like Du'a Generator whose modal was the first thing to
        // trigger a preload.
        if (!this.isAdLoaded) {
          console.log('[RewardedAd] No ad loaded, loading now...');
          this.preloadAd(); // fire-and-forget; wait loop below observes isAdLoaded
          const started = Date.now();
          while (!this.isAdLoaded && Date.now() - started < 8000) {
            await new Promise(resolve => setTimeout(resolve, 250));
          }
        }

        if (!this.isAdLoaded) {
          console.error('[RewardedAd] Ad still not loaded after 8s wait');
          this.pendingRewardCallback = null; // clear stale callback so a later reward can't fire against a closed modal
          this.notifyListeners('failedToShow', { message: 'Ad not loaded' });
          return false;
        }

        await this.admob.showRewardVideoAd();
        return true;
      } else {
        // Web/Preview mode: Simulate ad with mock flow
        console.log('[RewardedAd] Web mode - simulating ad with 3s delay...');
        this.notifyListeners('mockAdRequested');
        
        // Simulate watching an ad (3 second delay)
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Grant the reward
        console.log('[RewardedAd] Mock ad complete - granting reward');
        incrementDailyAdCount();
        
        // Call the reward callback
        if (this.pendingRewardCallback) {
          await this.pendingRewardCallback();
          this.pendingRewardCallback = null;
        }
        
        this.notifyListeners('adClosed');
        return true;
      }
    } catch (error) {
      console.error('[RewardedAd] Show error:', error);
      this.notifyListeners('failedToShow', error);
      return false;
    }
  }

  /**
   * Check if an ad is ready to show
   */
  isReady() {
    if (!this.admob) {
      return true; // Web mode: always "ready"
    }
    return this.isAdLoaded && !this.isAdShowing;
  }

  /**
   * Add listener for ad events
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(event, data = null) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (e) {
        console.error('[RewardedAd] Listener error:', e);
      }
    });
  }

  /**
   * Cleanup - remove all listeners
   */
  async cleanup() {
    try {
      for (const handle of this.eventListenerHandles) {
        if (handle && typeof handle.remove === 'function') {
          await handle.remove();
        }
      }
      this.eventListenerHandles = [];
      this.listeners = [];
      this.pendingRewardCallback = null;
      console.log('[RewardedAd] Cleanup complete');
    } catch (error) {
      console.error('[RewardedAd] Cleanup error:', error);
    }
  }
}

// Singleton instance
export const rewardedAdService = new RewardedAdService();
export default rewardedAdService;
